import { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useTranslations } from '@/hooks/useTranslations';
import { getLocalizedPath } from '@/lib/localizedPath';
import { trackNavigation } from '@/hooks/useAnalytics';
import LanguageSwitcher from './LanguageSwitcher';
import logoBlack from '@/assets/logo-black.svg';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  // Tracks whether a real interaction (not the initial mount) opened the menu,
  // so we only restore focus to the trigger when the user actually closes it.
  const wasOpenRef = useRef(false);
  const {
    t,
    language
  } = useTranslations();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close menu on route change
  useEffect(() => {
    setIsMenuOpen(false);
  }, [location.pathname]);

  // Focus management: trap focus inside the open menu, return it to the
  // trigger on close, and close on Escape.
  useEffect(() => {
    if (!isMenuOpen) {
      if (wasOpenRef.current) {
        // Menu just closed → restore focus to the trigger button.
        triggerRef.current?.focus();
        wasOpenRef.current = false;
      }
      return;
    }

    wasOpenRef.current = true;

    const getFocusable = (): HTMLElement[] => {
      if (!menuRef.current) return [];
      return Array.from(
        menuRef.current.querySelectorAll<HTMLElement>(
          'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"]), input, select, textarea'
        )
      ).filter((el) => !el.hasAttribute('aria-hidden') && el.offsetParent !== null);
    };

    // Move initial focus to the first focusable element inside the menu.
    const focusables = getFocusable();
    focusables[0]?.focus();

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        setIsMenuOpen(false);
        return;
      }
      if (e.key !== 'Tab') return;

      const items = getFocusable();
      if (items.length === 0) return;
      const first = items[0];
      const last = items[items.length - 1];
      const active = document.activeElement as HTMLElement | null;

      // If focus has somehow escaped the menu (e.g., focus on body),
      // pull it back in.
      if (!menuRef.current?.contains(active) && active !== triggerRef.current) {
        e.preventDefault();
        first.focus();
        return;
      }

      if (e.shiftKey && active === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isMenuOpen]);

  const handleLogoClick = (e: React.MouseEvent) => {
    const isHomePage = location.pathname === '/' || location.pathname === '/en' || location.pathname === '/en/';
    if (isHomePage) {
      e.preventDefault();
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    }
  };

  // Helper function using centralized utility
  const localizedPath = (path: string) => getLocalizedPath(path, language);

  const navItems = [{
    name: t('nav.method', 'SYSTEM'),
    href: localizedPath('/system')
  }, {
    name: t('nav.courses', language === 'en' ? 'COURSES' : 'KURSE'),
    href: localizedPath('/courses')
  }, {
    name: t('nav.book', language === 'en' ? 'BOOK' : 'BUCH'),
    href: localizedPath('/book')
  }, {
    name: language === 'en' ? 'PROGRAMMES' : 'PROGRAMME',
    href: localizedPath('/speaking')
  }, {
    name: t('nav.knowledge', language === 'en' ? 'KNOWLEDGE' : 'WISSEN'),
    href: localizedPath('/knowledge')
  }, {
    name: language === 'en' ? 'ABOUT' : 'ÜBER MICH',
    href: localizedPath('/about')
  }];

  return (
    <header
      role="banner"
      aria-label={language === 'en' ? 'Site header' : 'Seitenkopf'}
      className={`fixed top-0 left-0 right-0 z-50 bg-softwhite/70 backdrop-blur-[20px] backdrop-saturate-200 supports-[backdrop-filter]:bg-softwhite/60 border-b border-white/30 transition-[box-shadow] duration-[260ms] ease-out ${isScrolled ? 'shadow-premium' : ''}`}
    >
      <div className="container-blog">
        <div className={`items-center justify-between flex flex-row gap-0 mx-[30px] my-0 px-0 py-0 transition-[height] duration-[260ms] ease-out ${isScrolled ? 'h-14' : 'h-20'}`}>
          {/* Logo */}
          <div className="flex-shrink-0">
            <Link
              to={localizedPath('/')}
              onClick={handleLogoClick}
              className="block group"
              aria-label={language === 'en' ? 'Dr. K - Home' : 'Dr. K - Startseite'}
              aria-current={location.pathname === '/' || location.pathname === '/en' || location.pathname === '/en/' ? 'page' : undefined}
            >
              <img src={logoBlack} alt="" role="presentation" className={`w-auto transition-[height,transform] duration-[260ms] ease-out group-hover:scale-[1.04] ${isScrolled ? 'h-10' : 'h-20'}`} />
            </Link>
          </div>

          {/* Desktop Navigation */}
          <nav
            id="primary-navigation"
            className="hidden lg:flex items-center"
            aria-label={language === 'en' ? 'Primary' : 'Hauptnavigation'}
          >
            <ul className="flex items-center gap-x-3" role="list">
              {navItems.map(item => {
                const isCurrent = location.pathname === item.href;
                return (
                  <li key={item.name} className="flex">
                    <Link
                      to={item.href}
                      aria-current={isCurrent ? 'page' : undefined}
                      className="inline-flex items-center px-2 py-2 text-graphit font-semibold text-typography-meta tracking-wide hover:text-waldgruen transition-colors duration-[160ms] ease-out relative after:content-[''] after:absolute after:left-2 after:right-2 after:bottom-1 after:h-0.5 after:scale-x-0 after:bg-waldgruen after:origin-bottom-right after:transition-transform after:duration-[160ms] after:ease-out hover:after:scale-x-100 hover:after:origin-bottom-left aria-[current=page]:text-waldgruen aria-[current=page]:after:scale-x-100"
                      onClick={() => trackNavigation(item.name)}
                    >
                      {item.name}
                    </Link>
                  </li>
                );
              })}
            </ul>
          </nav>

          {/* Language Switcher */}
          <div className="hidden lg:flex items-center" aria-label={language === 'en' ? 'Language selection' : 'Sprachauswahl'}>
            <LanguageSwitcher />
          </div>

          {/* Mobile Menu Button */}
          <Button
            ref={triggerRef}
            variant="ghost"
            size="sm"
            className="lg:hidden rounded-xl hover:bg-waldgruen/10"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            aria-label={
              language === 'en'
                ? (isMenuOpen ? 'Close navigation menu' : 'Open navigation menu')
                : (isMenuOpen ? 'Navigationsmenü schließen' : 'Navigationsmenü öffnen')
            }
            aria-expanded={isMenuOpen}
            aria-controls="mobile-navigation"
            aria-haspopup="menu"
          >
            {isMenuOpen ? <X className="h-5 w-5" aria-hidden="true" /> : <Menu className="h-5 w-5" aria-hidden="true" />}
          </Button>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <div
            id="mobile-navigation"
            ref={menuRef}
            role="dialog"
            aria-modal="true"
            aria-label={language === 'en' ? 'Navigation menu' : 'Navigationsmenü'}
            className="lg:hidden border-t border-graphit/10 py-6 px-2 bg-softwhite/95 backdrop-blur-[20px] flex flex-col gap-6"
          >
            <nav
              className="flex flex-col"
              aria-label={language === 'en' ? 'Mobile' : 'Mobile Navigation'}
            >
              <ul className="flex flex-col gap-y-2" role="list">
                {navItems.map(item => {
                  const isCurrent = location.pathname === item.href;
                  return (
                    <li key={item.name}>
                      <Link
                        to={item.href}
                        aria-current={isCurrent ? 'page' : undefined}
                        className="flex items-center text-typography-body font-semibold text-graphit hover:text-waldgruen hover:bg-waldgruen/5 transition-colors duration-[160ms] ease-out px-4 py-3 rounded-xl tracking-wide aria-[current=page]:text-waldgruen min-h-[44px]"
                        onClick={() => {
                          trackNavigation(`${item.name} (Mobile)`);
                          setIsMenuOpen(false);
                        }}
                      >
                        {item.name}
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </nav>

            <div className="px-4 pt-4 border-t border-graphit/10" aria-label={language === 'en' ? 'Language selection' : 'Sprachauswahl'}>
              <LanguageSwitcher />
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;